import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dateFormat from "dateformat";
import { FileService } from "src/utils/file.service";

import { CourseContext } from "../../../context";

import { TabBar } from "src/components/tabbar";
import { CallAssessmentParameterView } from "src/pages/coaching/components/callAssessmentParameterView";

import { fetchLearnerReport } from "src/redux/actions/coaching/simulation";

import { SimulationAttempt } from "src/types/coaching/courses";

import _ from "lodash";
import cx from "classnames"
import styles from "./simulationReportView.module.scss"

interface LearnerDetailsReportProps {
  learner: SimulationAttempt;
}

export const SimulationReportView: React.FC<LearnerDetailsReportProps> = ({
  learner,
}) => {

  const dispatch = useDispatch();

  const courseContext = useContext(CourseContext);

  const simulationReport = courseContext?.simulationReport;
  const closeSimulationReport = courseContext?.closeSimulationReport;
  const callRecording = courseContext?.callRecording;

  const [activeTab, setActiveTab] = useState(0);
  const [selectedAttempt, setSelectedAttempt] = useState(learner);

  const report = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.item.report", null),
  );

  const loading = useSelector((state: any) =>
    _.get(state.coaching, "simulations.learnerReport.loading", false),
  );

  const childLearningRecord = useMemo(() => {
    return _.get(simulationReport, "session.childLearningRecord", null);
  }, [simulationReport]);

  const parameters = report?.parameters ?? [];

  useEffect(() => {
    if (learner && childLearningRecord) {
      dispatch(
        fetchLearnerReport({
          id: childLearningRecord,
        })
      );
      setSelectedAttempt(learner);
    }
  }, [learner?.id, childLearningRecord]);

  if (!simulationReport) return null;

  const { attempt, session } = simulationReport;

  const handleOpenCallRecording = (session: any) => {
    if (!attempt || !session) return;

    courseContext?.openCallRecording?.({
      attempt: attempt,
      session,
    });
  };

  return (
    <div className={styles.learnerPerformance}>
      <div className={styles.back} onClick={closeSimulationReport}>
        <i className="paperflite-left-arrow"></i>
      </div>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>breakdown</div>
          <div className={styles.details}>
            <div className={styles.metadata}>
              call logged on {dateFormat(report?.startedDate, "dd mmm yyyy")},{" "}
              {dateFormat(report?.startedDate, "HH:MM:ss")}
            </div>
            <div className={styles.divider} />
            <div className={styles.metadata}>
              duration {FileService.formatDuration(report?.duration, "short")}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          {
            learner?.status === "COMPLETED" &&
            report?.status !== "in_sufficient_duration" && (
              <div className={styles.callRecording}>
                <button className={styles.action} onClick={(e) => {
                  e.stopPropagation();
                  handleOpenCallRecording(session);
                }}>
                  <i className="paperflite-waveform"></i>
                  <span>call recording</span>
                </button>
              </div>
            )}
        </div>
      </div>
      {loading && <ReportPlaceholder />}

      {!loading && report?.status === "in_sufficient_duration" && (
        <div className={styles.emptyMessage}>
          <div className={styles.title}>data unavailable</div>
        </div>
      )}

      {report?.status !== "in_sufficient_duration" && (
        <div className={styles.callAssessmentContainer}>
          <TabBar
            className={cx(styles.tabs)}
            gap={32}
            tabSize="equal"
            active={activeTab}
            onSelectionChange={(index) => setActiveTab(index)}
          >
            {parameters.map((parameter, index) => (
              <span
                key={parameter.uuid}
                className={cx(styles.tab, {
                  [styles.active]: activeTab === index,
                })}
              >
                <div className={styles.name}>
                  {parameter.name} ({Math.round(parameter.score)}/5)
                </div>
              </span>
            ))}
          </TabBar>
          <div className={styles.callAssessmentParamater}>
            {parameters.map(
              (parameter, index) =>
                activeTab === index && (
                  <div key={parameter.uuid}>
                    <CallAssessmentParameterView parameter={parameter} />
                  </div>
                ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ReportPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.callAssessmentContainer, styles.placeholder)}>
      <div className={cx(styles.tabs)}>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
        <div className={cx(styles.tab, styles.block)}></div>
      </div>

      <div className={cx(styles.verticalTabs, styles.placeholder)}>
        <span className={cx(styles.text, styles.block)}></span>
        <span className={cx(styles.text, styles.block)}></span>
        <span className={cx(styles.text, styles.block)}></span>
      </div>
    </div>
  );
};
